import * as React from 'react'
import { styled } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import type { BaseTextFieldProps } from '@mui/material/TextField'
import IconButton from './IconButton'
import { IconName } from '../utils/iconNameDict'
import { Box, InputAdornment, TextField as MuiTextField } from '@mui/material'
import Tooltip from './Tooltip'

export type TextFieldRef = {
	validate: () => boolean
	reset: () => void
}

const StyledIconButton = styled(IconButton)<{ color?: string }>(({ color }) => ({
	color: color || 'inherit',
}))

const TextField = React.forwardRef<
	TextFieldRef,
	{
		label?: string
		defaultValue?: string | number
		disabled?: boolean
		onChange?:
			| ((value: string | number, id?: string) => void)
			| React.Dispatch<React.SetStateAction<string>>
		m?: string
		variant?: BaseTextFieldProps['variant']
		flex?: boolean
		width?: string
		startLabel?: string
		startIconName?: IconName
		startIconNameColor?: string
		onClickStartIcon?: () => void
		endLabel?: string
		endIconName?: IconName
		endIconColor?: string
		endIconSx?: SxProps<Theme>
		backgroundColor?: string
		toolTip?: string | string[]
		type?: 'text' | 'number' | 'password' | 'date' | 'time'
		multiline?: boolean
		maxRows?: number
		placeHolder?: string
		onClickEndIcon?: () => void
		id?: string
		min?: number
		max?: number
		onlyDigits?: boolean
		size?: BaseTextFieldProps['size']
		autoFocus?: boolean
		// Enter löst es aus; Umschalt+Enter bricht in einem mehrzeiligen Feld weiter die Zeile um.
		onEnter?: () => void
		// Sagt Browser und Passwortmanagern, was das Feld ist, etwa `one-time-code` statt Passwort.
		autoComplete?: string
		inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
	}
>(
	(
		{
			label,
			defaultValue,
			disabled,
			onChange,
			m,
			variant,
			flex,
			width,
			startLabel,
			startIconName,
			startIconNameColor,
			onClickStartIcon,
			endLabel,
			endIconName,
			endIconColor,
			endIconSx,
			backgroundColor,
			toolTip,
			type = 'text',
			multiline,
			maxRows,
			placeHolder,
			onClickEndIcon,
			id,
			min,
			max,
			onlyDigits,
			size,
			autoFocus,
			onEnter,
			autoComplete,
			inputMode,
		},
		ref
	): JSX.Element => {
		const [value, setValue] = React.useState<string | number>(defaultValue ?? '')
		const [errorMessage, setErrorMessage] = React.useState<undefined | string>()

		React.useEffect(() => {
			setValue(defaultValue ?? '')
		}, [defaultValue])

		const emitChange = React.useCallback(
			(val: string | number) => {
				if (!onChange) return
				;(onChange as (value: string | number, id?: string) => void)(val, id)
			},
			[id, onChange]
		)

		const handleChange = React.useCallback(
			(eventOrValue) => {
				const rawValue =
					typeof eventOrValue === 'string' ? eventOrValue : eventOrValue?.target?.value ?? ''

				const sanitizeDigits = (val: string) => (onlyDigits ? val.replace(/\D/g, '') : val)

				if (type === 'number') {
					const sanitized = sanitizeDigits(rawValue)
					if (sanitized === '') {
						setValue('')
						emitChange('')
						setErrorMessage(undefined)
						return
					}
					const parsed = Number(sanitized)
					if (Number.isNaN(parsed)) return
					let clamped = parsed
					if (typeof min === 'number' && clamped < min) clamped = min
					if (typeof max === 'number' && clamped > max) clamped = max
					setErrorMessage(undefined)
					setValue(clamped)
					emitChange(clamped)
					return
				}

				let cleaned = sanitizeDigits(rawValue)
				if (cleaned !== '' && typeof min === 'number') {
					const num = Number(cleaned)
					if (!Number.isNaN(num) && num < min) cleaned = String(min)
				}
				if (cleaned !== '' && typeof max === 'number') {
					const num = Number(cleaned)
					if (!Number.isNaN(num) && num > max) cleaned = String(max)
				}
				setErrorMessage(undefined)
				setValue(cleaned)
				emitChange(cleaned)
			},
			[emitChange, max, min, onlyDigits, type]
		)

		// Links und rechts können zugleich ein Icon tragen, etwa Telefonsymbol und Diskette.
		const inputProps = React.useMemo(() => {
			const startAdornment = startLabel ? (
				<InputAdornment position="start">{startLabel}</InputAdornment>
			) : startIconName ? (
				<InputAdornment position="start">
					<StyledIconButton
						onClick={onClickStartIcon}
						iconName={startIconName}
						color={startIconNameColor}
					/>
				</InputAdornment>
			) : toolTip ? (
				<InputAdornment position="start">
					<Tooltip width="40rem" text={toolTip} />
				</InputAdornment>
			) : undefined
			const endAdornment = endLabel ? (
				<InputAdornment position="start">{endLabel}</InputAdornment>
			) : endIconName ? (
				<InputAdornment position="start">
					<IconButton
						iconName={endIconName}
						onClick={onClickEndIcon}
						iconColor={endIconColor}
						sx={endIconSx}
					/>
				</InputAdornment>
			) : undefined
			return startAdornment || endAdornment ? { startAdornment, endAdornment } : undefined
		}, [
			endIconColor,
			endIconName,
			endIconSx,
			endLabel,
			onClickEndIcon,
			onClickStartIcon,
			startIconName,
			startIconNameColor,
			startLabel,
			toolTip,
		])

		const mergedInputProps = React.useMemo(
			() => ({ style: { backgroundColor }, ...inputProps }),
			[backgroundColor, inputProps]
		)

		const htmlInputProps = React.useMemo(() => (inputMode ? { inputMode } : undefined), [inputMode])

		const handleKeyDown = React.useCallback(
			(event: React.KeyboardEvent) => {
				if (onEnter && event.key === 'Enter' && !event.shiftKey) {
					event.preventDefault()
					onEnter()
				}
			},
			[onEnter]
		)

		const handleValidate = React.useCallback(() => {
			let invalid = false
			if (!disabled && !value) {
				invalid = true
				setErrorMessage('Bitte ausfüllen')
			}
			return invalid
		}, [disabled, value])

		React.useImperativeHandle(
			ref,
			() => ({
				reset: () => handleChange(''),
				validate: handleValidate,
			}),
			[handleChange, handleValidate]
		)

		React.useEffect(() => {
			if (disabled) {
				setErrorMessage(undefined)
			}
		}, [disabled])

		return (
			<Box m={m} flex={flex ? '1' : undefined} width={width} ref={ref}>
				<MuiTextField
					label={label}
					value={value}
					fullWidth
					onChange={handleChange}
					InputProps={mergedInputProps}
					inputProps={htmlInputProps}
					autoComplete={autoComplete}
					disabled={disabled}
					type={type}
					variant={variant}
					error={Boolean(errorMessage)}
					helperText={errorMessage}
					multiline={multiline}
					maxRows={maxRows}
					placeholder={placeHolder}
					size={size}
					autoFocus={autoFocus}
					onKeyDown={onEnter ? handleKeyDown : undefined}
				/>
			</Box>
		)
	}
)

export default React.memo(TextField)
